import { extractTextFromTipTap } from "../../../../../src/shared/schemas/CharacterSchema.js";

export type MentionItem = {
  id: string;
  display: string;
  type: "character" | "worldbuilding" | "document" | "outline" | "folder" | "series";
  /** Second line in the # menu (e.g. series book title for cross-book chapters). */
  subtitle?: string;
  /** Label used after insertion in the editor chip and serialized markup. */
  mentionLabel?: string;
};

type CharacterLike = { id: string; displayName?: string; name?: unknown };
type WorldbuildingLike = { id: string; displayName?: string; name?: unknown };

/** Plain-text label for # mentions; falls back to TipTap `name` when `displayName` is still empty (e.g. right after create). */
export function characterMentionLabel(char: CharacterLike): string {
  const fromDisplay = (char.displayName ?? "").trim();
  if (fromDisplay) return fromDisplay;
  const fromName = char.name != null ? extractTextFromTipTap(char.name as Parameters<typeof extractTextFromTipTap>[0]) : "";
  const trimmed = fromName.trim();
  if (trimmed) return trimmed;
  return "Unnamed Character";
}

/** Same pattern as {@link characterMentionLabel} for worldbuilding entries. */
export function worldbuildingMentionLabel(entry: WorldbuildingLike): string {
  const fromDisplay = (entry.displayName ?? "").trim();
  if (fromDisplay) return fromDisplay;
  const fromName = entry.name != null ? extractTextFromTipTap(entry.name as Parameters<typeof extractTextFromTipTap>[0]) : "";
  const trimmed = fromName.trim();
  if (trimmed) return trimmed;
  return "Unnamed Entry";
}

type FolderLike = { id: string; name?: string };
type DocumentLike = { id: string; title?: string; bookTitle?: string };
type SeriesBookLike = { projectId: string; title?: string; seriesOrder?: number | null };
type SeriesBookDocumentLike = { id: string; title?: string; bookProjectId: string; bookTitle?: string };

/** Default cap for # mention dropdowns; list UI scrolls so users can reach every match. */
export const MENTION_DEFAULT_MAX_RESULTS = 10_000;

export type MentionSearchOptions = {
  characters?: CharacterLike[];
  worldbuilding?: WorldbuildingLike[];
  documents?: DocumentLike[];
  folders?: FolderLike[];
  seriesBooks?: SeriesBookLike[];
  seriesBookDocuments?: SeriesBookDocumentLike[];
  hasOutline?: boolean;
  isStoryBibleActive?: boolean;
  maxResults?: number;
};

export function filterMentionItems(query: string, options: MentionSearchOptions): MentionItem[] {
  const {
    characters = [],
    worldbuilding = [],
    documents = [],
    folders = [],
    seriesBooks = [],
    seriesBookDocuments = [],
    hasOutline = false,
    isStoryBibleActive = false,
    maxResults = MENTION_DEFAULT_MAX_RESULTS,
  } = options;

  // Keep matches stable while users type trailing spaces in `#query`.
  const searchQuery = query.toLowerCase().trimEnd();

  const outlineItem: MentionItem[] =
    hasOutline && "outline".includes(searchQuery)
      ? [{ id: "outline::current", display: "Outline", type: "outline" }]
      : [];

  const characterItems: MentionItem[] = characters
    .filter((char) => characterMentionLabel(char).toLowerCase().includes(searchQuery))
    .map((char) => ({
      id: `character::${char.id}`,
      display: characterMentionLabel(char),
      type: "character" as const,
    }));

  const worldbuildingItems: MentionItem[] = worldbuilding
    .filter((entry) => worldbuildingMentionLabel(entry).toLowerCase().includes(searchQuery))
    .map((entry) => ({
      id: `worldbuilding::${entry.id}`,
      display: worldbuildingMentionLabel(entry),
      type: "worldbuilding" as const,
    }));

  const documentItems: MentionItem[] = documents
    .filter((doc) => (doc.title || "").toLowerCase().includes(searchQuery))
    .map((doc) => {
      const bookTitle = typeof doc.bookTitle === "string" ? doc.bookTitle.trim() : "";
      return {
        id: `document::${doc.id}`,
        display: doc.title || "Untitled Document",
        type: "document" as const,
        ...(bookTitle ? { subtitle: bookTitle } : {}),
        ...(bookTitle ? { mentionLabel: `${doc.title || "Untitled Document"} (${bookTitle})` } : {}),
      };
    });

  // Series book documents — chapters from other books (subtitle shows which book)
  const seriesBookDocumentItems: MentionItem[] = seriesBookDocuments
    .filter((doc) => (doc.title || "").toLowerCase().includes(searchQuery))
    .map((doc) => ({
      id: `series_document::${doc.bookProjectId}::${doc.id}`,
      display: doc.title || "Untitled Document",
      type: "document" as const,
      subtitle: doc.bookTitle?.trim() || "Series Book",
      mentionLabel: `${doc.title || "Untitled Document"} (${doc.bookTitle?.trim() || "Series Book"})`,
    }));

  const seriesBookItems: MentionItem[] = seriesBooks
    .filter((book) => (book.title || "").toLowerCase().includes(searchQuery))
    .sort((a, b) => {
      const aOrder = a.seriesOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.seriesOrder ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.title || "").localeCompare(b.title || "");
    })
    .map((book) => ({
      id: `series::${book.projectId}`,
      display: book.title || "Untitled Book",
      type: "series" as const,
    }));

  const folderItems: MentionItem[] = folders
    .filter((folder) => (folder.name || "").toLowerCase().includes(searchQuery))
    .map((folder) => ({
      id: `folder::${folder.id}`,
      display: folder.name || "Untitled Folder",
      type: "folder" as const,
    }));

  if (isStoryBibleActive) {
    return [...outlineItem, ...characterItems, ...worldbuildingItems, ...documentItems, ...folderItems, ...seriesBookDocumentItems, ...seriesBookItems].slice(0, maxResults);
  }
  return [...outlineItem, ...documentItems, ...folderItems, ...seriesBookDocumentItems, ...seriesBookItems].slice(0, maxResults);
}
